import type { UseQueryResult } from '@tanstack/react-query';
import { subDays } from 'date-fns';

import { type TransactionTableResponse } from '../../banking/types/Transactions';
import { buildQueryParams } from '../../common/misc';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { DEFAULT_DATE_FORMAT, format } from '../../common/utils/dateUtils';
import { requestBuilder } from '../../http/RequestBuilder';
import {
    type AccountSummary,
    Provider,
    type Transaction,
} from '../types/HolisticSummary';

export interface LinkedTransactionsResponse {
    total: number;
    page: number;
    size: number;
    transactions: Transaction[];
}

const getLinkedAccountTransactions = requestBuilder.query<
    LinkedTransactionsResponse,
    {
        userId: string;
        accountId: string;
        limit: number;
        provider: Provider;
    },
    Error,
    Transaction[]
>({
    endpoint: ({ accountId, provider }) =>
        `/api/linked-accounts/${accountId}/transactions${buildQueryParams({
            provider,
        })}`,
    queryKey: ({ userId, accountId }) =>
        `getLinkedAccountTransactions/${userId}/${accountId}`,
});

const getSyncteraTransactionsV2 = requestBuilder.query<
    TransactionTableResponse,
    {
        userId: string;
        bankAccountId: string;
        limit: number;
        fromDate: Date;
        toDate: Date;
    },
    Error,
    Transaction[]
>({
    endpoint: ({ bankAccountId, limit, fromDate, toDate }) =>
        `/api/transactions/table/${bankAccountId}${buildQueryParams({
            limit,
            from_date: format(fromDate, DEFAULT_DATE_FORMAT),
            to_date: format(toDate, DEFAULT_DATE_FORMAT),
        })}`,
    queryKey: ({ userId, bankAccountId }) =>
        `getAccountTransactionsV2/${userId}/${bankAccountId}`,
});

export const useGetHolisticTransactions = (account?: AccountSummary) => {
    const { id: userId } = useAuthedUser();

    const isQuilttAccount = account?.provider === Provider.QUILTT;
    const isSyncteraAccount =
        account?.provider === Provider.SYNCTERA &&
        account?.bankAccountId != null;

    const linkedAccountQueryResult = getLinkedAccountTransactions(
        {
            userId,
            accountId: account?.id ?? '',
            limit: 10,
            provider: Provider.QUILTT,
        },
        {
            retry: false,
            select: (data: LinkedTransactionsResponse) => {
                return data.transactions;
            },
            enabled: isQuilttAccount,
        },
    );

    const fromDate = subDays(new Date(), 90);
    const toDate = new Date();

    const syncteraQueryResult: UseQueryResult<Transaction[], Error> =
        getSyncteraTransactionsV2(
            {
                userId,
                bankAccountId: account?.bankAccountId ?? '',
                limit: 50,
                fromDate,
                toDate,
            },
            {
                retry: false,
                select: ({ postedTransactions }: TransactionTableResponse) => {
                    return postedTransactions.map((t) => ({
                        id: t.uuid,
                        amount: t.amount,
                        date: t.postedDate as string, // Can expect this to exist for posted transactions
                        description: t.description,
                        rawDescription: t.description,
                        accountId: t.uuid,
                        type: t.dcSign,
                        status: t.status,
                        pending: t.status === 'pending',
                    }));
                },
                enabled: isSyncteraAccount,
            },
        );

    return isSyncteraAccount ? syncteraQueryResult : linkedAccountQueryResult;
};
